/**
 * Pure keyboard model for the Sources tab tree (LG-312): the WAI-ARIA tree
 * pattern's key handling over a flattened list of visible rows. The component
 * owns focus and expansion state; everything here just answers "given these
 * visible rows, what should this key do?" so the interaction contract is
 * testable without a DOM.
 *
 * Nodes are generic over a payload (LG-827): the component builds ONE forest
 * carrying its domain values and derives both the rendered rows and the
 * keyboard rows from it, so the two can never disagree. This module never
 * reads the payload — it only carries it through.
 */

/** One node of the tree as the nav model sees it, children in visual order. */
export interface TreeNavNode<P> {
  id: string
  /** The row's human title — what type-ahead matches on. */
  title: string
  payload: P
  children: TreeNavNode<P>[]
}

/** One visible row, in visual (top-to-bottom) order. */
export interface VisibleTreeRow<P> {
  id: string
  title: string
  /** The owning node's payload, by reference. */
  payload: P
  parentId: string | null
  /** 1-based, for `aria-level`. */
  level: number
  hasChildren: boolean
  /** Only ever true when `hasChildren` is. */
  expanded: boolean
}

export type TreeNavCommand =
  | { kind: 'focus'; id: string }
  | { kind: 'expand'; id: string }
  | { kind: 'collapse'; id: string }
  | { kind: 'none' }

const NONE: TreeNavCommand = { kind: 'none' }

/**
 * Flatten the forest into the rows a user can currently see: a node is
 * visible iff every ancestor is in `openIds`. An open marker on a node whose
 * ancestor is closed is retained state, not visibility.
 */
export function flattenVisibleRows<P>(
  forest: readonly TreeNavNode<P>[],
  openIds: ReadonlySet<string>,
): VisibleTreeRow<P>[] {
  const rows: VisibleTreeRow<P>[] = []
  const visit = (
    nodes: readonly TreeNavNode<P>[],
    parentId: string | null,
    level: number,
  ): void => {
    for (const node of nodes) {
      const hasChildren = node.children.length > 0
      const expanded = hasChildren && openIds.has(node.id)
      rows.push({
        id: node.id,
        title: node.title,
        payload: node.payload,
        parentId,
        level,
        hasChildren,
        expanded,
      })
      if (expanded) visit(node.children, node.id, level + 1)
    }
  }
  visit(forest, null, 1)
  return rows
}

/** Every node with children, at any depth — what "expand all" opens. */
export function expandableIdsOf<P>(
  forest: readonly TreeNavNode<P>[],
): Set<string> {
  const ids = new Set<string>()
  const visit = (nodes: readonly TreeNavNode<P>[]): void => {
    for (const node of nodes) {
      if (node.children.length > 0) {
        ids.add(node.id)
        visit(node.children)
      }
    }
  }
  visit(forest)
  return ids
}

const NAV_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'ArrowRight',
  'ArrowLeft',
  'Home',
  'End',
  'Enter',
  ' ',
])

/**
 * What `key` should do with `activeId` focused. Null means the tree does not
 * own this key at all — the component must leave the event alone. A non-null
 * command is consumed (preventDefault) even when it is `{ kind: 'none' }`:
 * Space on a leaf must not scroll the pane out from under the focused row.
 */
export function treeKeyCommand<P>(
  rows: readonly VisibleTreeRow<P>[],
  activeId: string,
  key: string,
): TreeNavCommand | null {
  if (!NAV_KEYS.has(key)) return null
  if (rows.length === 0) return NONE

  const index = rows.findIndex((row) => row.id === activeId)
  // The focused node can vanish under us (collapsed elsewhere, refetch);
  // land on the first row rather than dead-ending the keyboard.
  if (index === -1) return { kind: 'focus', id: rows[0].id }
  const row = rows[index]

  switch (key) {
    case 'ArrowDown':
      return index + 1 < rows.length
        ? { kind: 'focus', id: rows[index + 1].id }
        : NONE
    case 'ArrowUp':
      return index > 0 ? { kind: 'focus', id: rows[index - 1].id } : NONE
    case 'ArrowRight':
      if (!row.hasChildren) return NONE
      if (!row.expanded) return { kind: 'expand', id: row.id }
      // Open with children: the first child is the very next visible row.
      return { kind: 'focus', id: rows[index + 1].id }
    case 'ArrowLeft':
      if (row.expanded) return { kind: 'collapse', id: row.id }
      return row.parentId === null ? NONE : { kind: 'focus', id: row.parentId }
    case 'Home':
      return { kind: 'focus', id: rows[0].id }
    case 'End':
      return { kind: 'focus', id: rows[rows.length - 1].id }
    // Toggle, the tree's default action. Explicit cases (not the default
    // arm) so a key added to NAV_KEYS without its own case is a no-op
    // instead of silently becoming a toggle.
    case 'Enter':
    case ' ':
      if (!row.hasChildren) return NONE
      return row.expanded
        ? { kind: 'collapse', id: row.id }
        : { kind: 'expand', id: row.id }
    default:
      return NONE
  }
}

/** The one matching rule type-ahead uses, everywhere it matches. */
const titleStartsWith = (title: string, prefix: string): boolean =>
  title.toLowerCase().startsWith(prefix.toLowerCase())

/**
 * Type-ahead: the next visible row (searching forward from the row after the
 * active one, wrapping) whose title starts with the typed prefix.
 */
function typeaheadTargetId<P>(
  rows: readonly VisibleTreeRow<P>[],
  activeId: string,
  prefix: string,
): string | null {
  if (rows.length === 0 || prefix.length === 0) return null
  const start = rows.findIndex((row) => row.id === activeId)
  for (let offset = 1; offset <= rows.length; offset++) {
    const row = rows[(start + offset) % rows.length]
    if (titleStartsWith(row.title, prefix)) return row.id
  }
  return null
}

/** How long a pause splits two keystrokes into two type-ahead searches. */
export const TYPEAHEAD_RESET_MS = 600

export interface TypeaheadState {
  buffer: string
  /** Timestamp of the last keystroke, `Date.now()` epoch ms. */
  at: number
}

/**
 * Feed one printable keystroke into the type-ahead buffer and resolve where
 * focus should go. Beyond plain prefix accumulation this covers two APG
 * conventions the raw buffer would break: a multi-char refinement may stay on
 * the active row (typing "ch" after "c" landed on a C-row must not skip it),
 * and when the grown buffer matches nothing the newest letter searches alone
 * — which is exactly same-letter cycling ("c","c" walks the C-rows) and also
 * rescues a buffer poisoned by a missed key ("z","c" still finds a C-row).
 */
export function advanceTypeahead<P>(
  rows: readonly VisibleTreeRow<P>[],
  activeId: string,
  state: TypeaheadState,
  char: string,
  now: number,
): { state: TypeaheadState; targetId: string | null } {
  const grown = now - state.at > TYPEAHEAD_RESET_MS ? char : state.buffer + char

  if (grown.length > 1) {
    const active = rows.find((row) => row.id === activeId)
    if (active && titleStartsWith(active.title, grown)) {
      return { state: { buffer: grown, at: now }, targetId: active.id }
    }
    const target = typeaheadTargetId(rows, activeId, grown)
    if (target !== null) {
      return { state: { buffer: grown, at: now }, targetId: target }
    }
    // Fall through: the accumulated buffer is dead, restart from `char`.
  }

  return {
    state: { buffer: char, at: now },
    targetId: typeaheadTargetId(rows, activeId, char),
  }
}
